import { useEffect, useState } from "react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { IndianRupee, CalendarCheck, TrendingUp, Users, MessageSquare, Sparkles } from "lucide-react";
import { getAnalytics, apiErr } from "../lib/api";
import { PageHead, Stat, Empty } from "./_ui";
import { toast } from "sonner";

const BARS = ["#D84315", "#6D8B44", "#2F7A6B", "#6A4EA8", "#B98A2E", "#C1440E"];
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { getAnalytics().then(setD).catch((e) => toast.error(apiErr(e))); }, []);
  const k = d?.kpis || {};
  const tickEvery = Math.ceil((d?.trend?.length || 1) / 7);

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-7 md:px-8" data-testid="admin-analytics">
      <PageHead eyebrow="Marketplace performance" title="Analytics"
        desc="Demand, conversion and revenue across every ceremony request and booking." />

      <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={CalendarCheck} label="Ceremony requests" value={k.totalRequests ?? 0} sub={`${k.bookedRequests ?? 0} converted to bookings`} />
        <Stat icon={TrendingUp} tint={{ bg: "#EEF2E6", fg: "#6D8B44" }} label="Conversion" value={`${k.conversion ?? 0}%`} sub="Requests that became bookings" />
        <Stat icon={IndianRupee} tint={{ bg: "#E6F0EE", fg: "#2F7A6B" }} label="Gross revenue" value={inr(k.revenue)} sub={`Avg ${inr(k.avgBooking)} / booking`} />
        <Stat icon={IndianRupee} tint={{ bg: "#F0ECF7", fg: "#6A4EA8" }} label="Platform fee" value={inr(k.platformFee)} sub={`At ${k.feePct ?? 10}% take rate`} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="rounded-2xl border border-[#E9E2D6] bg-white p-5 lg:col-span-2" data-testid="analytics-trend">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold text-[#2C241B]">Revenue & requests · 14 days</h2>
            <div className="flex items-center gap-4 font-body text-xs text-[#8A8072]">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#2F7A6B]" /> Revenue</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#D84315]" /> Requests</span>
            </div>
          </div>
          <div className="mt-5 h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d?.trend || []} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#F0EBE1" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} interval={tickEvery} tick={{ fill: "#A79C8A", fontSize: 11 }} />
                <YAxis yAxisId="l" tickLine={false} axisLine={false} tick={{ fill: "#A79C8A", fontSize: 11 }} />
                <YAxis yAxisId="r" orientation="right" tickLine={false} axisLine={false} allowDecimals={false} tick={{ fill: "#A79C8A", fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E9E2D6", fontSize: 12 }} />
                <Line yAxisId="l" type="monotone" dataKey="revenue" stroke="#2F7A6B" strokeWidth={2} dot={false} />
                <Line yAxisId="r" type="monotone" dataKey="requests" stroke="#D84315" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E9E2D6] bg-white p-5" data-testid="analytics-top-poojas">
          <h2 className="font-heading text-xl font-semibold text-[#2C241B]">Top requested poojas</h2>
          {(d?.topPoojas || []).length === 0 ? (
            <div className="mt-5"><Empty>No requests yet.</Empty></div>
          ) : (
            <div className="mt-4 h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <BarChart data={d.topPoojas} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
                  <XAxis type="number" hide allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={110} tickLine={false} axisLine={false} tick={{ fill: "#5C5449", fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #E9E2D6", fontSize: 12 }} cursor={{ fill: "#FAF6EF" }} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                    {d.topPoojas.map((_, i) => <Cell key={i} fill={BARS[i % BARS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="rounded-2xl border border-[#E9E2D6] bg-white p-5">
          <h2 className="font-heading text-lg font-semibold text-[#2C241B]">Request funnel</h2>
          <div className="mt-4 space-y-3">
            {Object.entries(d?.requestFunnel || {}).map(([s, c]) => (
              <div key={s} className="flex items-center justify-between font-body text-sm">
                <span className="capitalize text-[#5C5449]">{s}</span>
                <span className="font-heading text-lg font-semibold text-[#2C241B]">{c}</span>
              </div>
            ))}
            {Object.keys(d?.requestFunnel || {}).length === 0 && <Empty>No data.</Empty>}
          </div>
        </div>

        <div className="rounded-2xl border border-[#E9E2D6] bg-white p-5" data-testid="analytics-chat">
          <div className="flex items-center gap-2"><MessageSquare size={16} className="text-[#D84315]" /><h2 className="font-heading text-lg font-semibold text-[#2C241B]">Assistant engagement</h2></div>
          <div className="mt-4 grid grid-cols-2 gap-3 font-body text-sm">
            <div><p className="font-heading text-2xl font-semibold text-[#2C241B]">{d?.chat?.sessions ?? 0}</p><p className="text-xs text-[#A79C8A]">Chat sessions</p></div>
            <div><p className="font-heading text-2xl font-semibold text-[#2C241B]">{d?.chat?.contacts ?? 0}</p><p className="text-xs text-[#A79C8A]">Shared contact</p></div>
            <div><p className="font-heading text-2xl font-semibold text-[#2C241B]">{d?.chat?.messages ?? 0}</p><p className="text-xs text-[#A79C8A]">Site messages</p></div>
            <div><p className="flex items-center gap-1 font-heading text-2xl font-semibold text-[#2C241B]"><Sparkles size={16} className="text-[#C9A24A]" />{d?.chat?.puromitra ?? 0}</p><p className="text-xs text-[#A79C8A]">PuroMitra messages</p></div>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E9E2D6] bg-white p-5">
          <div className="flex items-center gap-2"><Users size={16} className="text-[#6D8B44]" /><h2 className="font-heading text-lg font-semibold text-[#2C241B]">Supply languages</h2></div>
          <div className="mt-4 space-y-2">
            {(d?.languages || []).map((l) => (
              <div key={l.name} className="flex items-center justify-between font-body text-sm">
                <span className="text-[#5C5449]">{l.name}</span>
                <span className="font-semibold text-[#2C241B]">{l.count} purohits</span>
              </div>
            ))}
            {(d?.languages || []).length === 0 && <Empty>No purohits yet.</Empty>}
          </div>
        </div>
      </div>
    </div>
  );
}
